/**
 * Iter XX.72.6 — Canonical Gully Plus purchase flow.
 *
 * ONE function used by every Plus CTA across the app:
 *   • PaywallModal "Continue with Gully Plus"
 *   • Upgrade.jsx "Start Improving Every Match" / "Resume subscription"
 *   • Chat.jsx IdentitySheet.onLinked (paywall continuation)
 *
 * Guarantees:
 *   - Anonymous users are routed to identity capture first, then land
 *     back at checkout WITHOUT a second click.
 *   - `session_id`-less identity capture never surfaces "session not
 *     found" (backend contract in Iter XX.72.6).
 *   - Client-side single-flight: an in-flight ref + module-level guard
 *     stop double-taps AND React StrictMode double-effect re-entry.
 *   - Server-side dedupe reuses an OPEN Stripe Session for the same
 *     user within 10 minutes (plus_billing.CHECKOUT_DEDUPE_WINDOW_SECONDS).
 *   - Full funnel analytics emitted to /api/web/track:
 *       plus_cta_clicked
 *       plus_identity_required   (anonymous branch)
 *       plus_identity_linked     (post-/web/identity — fired by Chat.jsx)
 *       checkout_session_requested
 *       checkout_session_created
 *       checkout_redirect_started
 *       checkout_creation_failed (with reason)
 *   - Canonical acquisition chain emitted to /api/acquisition/track
 *     (Iter XX.285f, 2026-10-02 — founder-mandated terminology:
 *     `purchase_started` MEANS "purchase CTA tapped", NOT "checkout
 *     started"; do NOT count it as a checkout attempt):
 *       purchase_cta_tapped → checkout_request_started →
 *       checkout_session_created → checkout_opened →
 *       payment_completed / checkout_cancelled / checkout_failed
 *     (`purchase_started` dual-emitted as a deprecated alias of
 *     `purchase_cta_tapped` until 2026-10-09.)
 *   - Never toasts "not_authenticated". On 401 fallback, we route to
 *     identity capture.
 *
 * Return contract:
 *   { ok: true } when a Stripe redirect has been initiated OR the
 *   anonymous identity redirect has been initiated.
 *   { ok: false, reason } when we bailed early (e.g. pricing not ready).
 */
import api from "./api";
import { toast } from "sonner";
import { markCheckoutOpened } from "./checkoutBeacon";
import { hasToken, clearToken } from "./nativeTokenStorage";
import { isNative, isIOS } from "./nativePlatform";
import {
  isRevenueCatReady,
  purchasePlusMonthly,
  getMonthlyPackage,
  getConfigureReason,
} from "./revenueCat";
import { trackMetaFunnelEvent } from "./metaFunnelEvents";
import { getBuildInfo, getBuildInfoSync } from "./nativeAppInfo";
import {
  logPurchaseStarted, logPurchaseCompleted, logPurchaseCancelled,
} from "./iosMetaEvents";

// Module-level single-flight guard. React StrictMode + double-tap
// covered in one place, independent of any component ref lifetime.
let _inFlight = false;

function _now() { return Date.now(); }

function _hasToken() {
  try { return hasToken(); }
  catch { return false; }
}

function _getSessionId() {
  try { return localStorage.getItem("gully_web_session_id") || null; }
  catch { return null; }
}

/**
 * Fire an analytics event to /api/web/track.
 * Session id is now optional on the backend (Iter XX.72.6) so this
 * works from any surface — even pre-session Plus pages.
 */
function _track(event, payload) {
  try {
    api.post("/web/track", {
      session_id: _getSessionId(),
      event,
      payload: payload || {},
    }).catch(() => {});
  } catch { /* swallow */ }
}

/**
 * Kick off the Plus purchase from any entry point in the app.
 *
 * @param {Object} opts
 * @param {string} opts.entryPoint  e.g. "paywall_modal", "plus_page",
 *   "post_identity_link", "chat_upgrade_query".
 * @param {boolean} [opts.priceLoaded=true]  If the caller is Upgrade.jsx,
 *   it should pass its own priceLoaded so we short-circuit before ever
 *   hitting /billing/checkout. PaywallModal / IdentitySheet callers
 *   don't need this — they defer to the caller's own guards.
 * @param {string} [opts.country]  Country code hint for pricing.
 * @param {boolean} [opts.forceRefreshInFlight=false]  Escape hatch to
 *   force-clear the in-flight guard (used by the autostart useEffect
 *   after a redirect round-trip).
 */

// ---------------------------------------------------------------------------
// Iter XX.285e — Approved purchase-failure telemetry spec (2026-09-26).
// Helpers to enrich purchase events with platform, app/build version,
// product, and sanitized error signals. All best-effort, never throw.
// ---------------------------------------------------------------------------

/**
 * Gather platform + app/build version + product for telemetry.
 * Synchronous best-effort: native version fields are filled async
 * via getBuildInfo() elsewhere; here we use the sync cached values
 * if available, else null. Never throws.
 */
function _gatherPurchaseTelemetry() {
  let platform = "web";
  try {
    if (isNative()) {
      platform = isIOS() ? "ios" : "android";
    }
  } catch { /* noop */ }

  let app_version = null;
  let build_number = null;
  try {
    const bi = getBuildInfoSync();
    if (bi) {
      app_version = bi.app_version || null;
      build_number = bi.app_build || bi.build_number || null;
    }
  } catch { /* noop */ }

  let product_id = "plus_monthly_native";
  try {
    product_id = isIOS() ? "plus_monthly_ios" : "plus_monthly_android";
    if (!isNative()) product_id = "plus_monthly_web";
  } catch { /* noop */ }

  return { platform, app_version, build_number, product_id };
}

/**
 * Sanitize an error message for telemetry. Strips anything that looks
 * like PII (emails, tokens, long hex strings) and truncates to 200 chars.
 * Never throws; returns null on unusable input.
 */
function _sanitizeErrorMessage(msg) {
  try {
    if (msg == null) return null;
    let s = String(msg);
    // Strip emails
    s = s.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "[email]");
    // Strip long hex/token-like strings (32+ chars)
    s = s.replace(/\b[a-fA-F0-9]{32,}\b/g, "[token]");
    // Strip bearer tokens
    s = s.replace(/bearer\s+[a-zA-Z0-9._-]+/gi, "bearer [redacted]");
    s = s.trim().slice(0, 200);
    return s || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Iter XX.285f — Purchase event chain + terminology fix (2026-10-02).
//
// Founder-mandated: `purchase_started` MEANS "purchase CTA tapped" — it
// is NOT a checkout attempt. New canonical chain:
//   purchase_cta_tapped → checkout_request_started →
//   checkout_session_created → checkout_opened →
//   payment_completed / checkout_cancelled / checkout_failed
//
// `_emitCtaTapped` dual-emits the deprecated `purchase_started` alias
// alongside `purchase_cta_tapped` until 2026-10-09 so existing
// dashboards don't break during migration. After that date, delete the
// `purchase_started` call and its CLIENT_TRACKABLE entry.
// ---------------------------------------------------------------------------

/**
 * Emit the CTA-tap milestone: canonical `purchase_cta_tapped` plus the
 * deprecated `purchase_started` dual-emit. Fire-and-forget, never throws.
 */
function _emitCtaTapped({ milestoneKey, extra }) {
  try {
    trackMetaFunnelEvent("purchase_cta_tapped", {
      milestone_key: milestoneKey,
      extra,
    });
  } catch { /* fire-and-forget */ }
  try {
    // DEPRECATED dual-emit — remove after 2026-10-09.
    trackMetaFunnelEvent("purchase_started", {
      milestone_key: milestoneKey,
      extra: { ...extra, deprecated_alias_of: "purchase_cta_tapped" },
    });
  } catch { /* fire-and-forget */ }
}

/**
 * Emit a canonical chain event (checkout_request_started, checkout_opened,
 * checkout_cancelled, payment_completed). Fire-and-forget, never throws.
 */
function _emitChainEvent(eventName, { milestoneKey, extra }) {
  try {
    trackMetaFunnelEvent(eventName, { milestone_key: milestoneKey, extra });
  } catch { /* fire-and-forget */ }
}

export async function beginPlusPurchase({
  entryPoint = "unknown",
  priceLoaded = true,
  country,
  forceRefreshInFlight = false,
  variant = null,
} = {}) {
  if (forceRefreshInFlight) _inFlight = false;
  if (_inFlight) return { ok: false, reason: "in_flight" };
  _inFlight = true;

  // Iter XX.181 — App Store 3.1.1 compliance guard.
  //
  // On Capacitor iOS / Android, any digital-subscription purchase MUST
  // go through StoreKit / Google Play Billing, not through our Stripe
  // web checkout. Native IAP wiring lands in Phase 2 (see
  // memory/MOBILE_PHASE_2_PLAN.md). Until that ships, every purchase
  // entry point on native short-circuits to a friendly notice — no
  // Stripe redirect, no web checkout URL, no external link. This
  // preserves the coaching experience and keeps App Review happy.
  //
  // Web behavior is completely unchanged (isNative() === false on web).
  // Iter XX.197 — Native purchase now goes through Google Play Billing
  // via RevenueCat (Feb 2026). No Stripe redirect, no external link,
  // no /plus navigation on native. If RevenueCat is not yet
  // configured (missing key / older build), we fall back to the
  // XX.181 friendly notice to avoid breaking the user's session.
  //
  // Web behaviour is completely unchanged (isNative() === false on web
  // → this whole block is skipped and we continue to Stripe checkout).
  if (isNative()) {
    if (!isRevenueCatReady()) {
      // Iter XX.295 — Truthful unavailable state (commercial P0).
      // The precise configure reason travels with the blocked event so
      // production can distinguish a deterministic build defect
      // (rc_key_missing → build/release P0, alert) from a transient
      // (rc_configuring → user can retry in a moment). The toast tells
      // the truth about THIS build — never "being turned on".
      const blockedReason = getConfigureReason() || "rc_not_ready";
      getBuildInfo()
        .then((build) => {
          _track("plus_cta_clicked_native_blocked", {
            entry: entryPoint,
            reason: blockedReason,
            build_id: build.build_id,
            app_version: build.app_version,
            app_build: build.app_build,
          });
        })
        .catch(() => {});
      _inFlight = false;
      try {
        if (blockedReason === "rc_configuring") {
          toast.message("One moment…", {
            description:
              "Setting up subscriptions — please try again in a few seconds.",
          });
        } else {
          toast.message("Subscriptions aren't available in this version.", {
            description:
              "Update Gully to the latest version to subscribe to Plus.",
            duration: 6000,
          });
        }
      } catch { /* noop — toast not mounted */ }
      return { ok: false, reason: "native_iap_pending" };
    }

    _track("plus_cta_clicked_native", { entry: entryPoint });
    // Iter XX.229 — TEMPORARY purchase-flow diagnostic logging.
    // All lines share the tag `[gully-purchase-diag]` for grep. Remove
    // this block once the sandbox purchase failure is diagnosed.
    // Diagnostic-only: no logic change, no fix.
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] handler invoked", {
      entryPoint,
      priceLoaded,
      country: country || null,
      is_native: true,
      is_ios: (() => { try { return isIOS(); } catch { return "unknown"; } })(),
      rc_ready: (() => { try { return isRevenueCatReady(); } catch { return "unknown"; } })(),
    });
    // Iter XX.198 — canonical purchase CTA tap (native path).
    // Iter XX.282 — enriched with product_id / offer_id / source /
    // variant so Muse / XX.277 can split the funnel by paywall variant
    // and native product. Native platform tag is added server-side.
    // Iter XX.285f — TERMINOLOGY FIX: this event means "CTA tapped",
    // NOT "checkout started". Dual-emits deprecated `purchase_started`
    // alias until 2026-10-09.
    _emitCtaTapped({
      milestoneKey: "native_iap",
      extra: {
        entry:      entryPoint,
        channel:    "revenuecat",
        product_id: (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
        offer_id:   "plus_default_v1",
        source:     entryPoint,
        variant:    variant,
      },
    });
    // Iter XX.224 — iOS Meta App Events. Consent-gated, iOS-only,
    // fires-and-forgets. Never blocks purchase.
    try { logPurchaseStarted({ product_id: "plus_monthly_ios", period: "monthly", surface: entryPoint }); } catch {}

    // Iter XX.224 (follow-up) — Capture RevenueCat package price +
    // currency BEFORE the native purchase sheet opens so the eventual
    // Meta `gully_purchase_completed` event can carry truthful value
    // + currency. Meta uses these to optimise Ads bidding toward
    // commercial value. RevenueCat client caches offerings, so this
    // is a cheap in-memory read on the happy path. On any failure we
    // still fire the completion event, just without value/currency —
    // per founder decision (Feb 2026) truthful attribution beats a
    // fabricated fallback (no value=0, no missing-currency guess).
    let _rcPriceValue;
    let _rcPriceCurrency;
    try {
      const _pkg = await getMonthlyPackage();
      const _price = _pkg?.product?.price;
      const _currencyCode = _pkg?.product?.currencyCode;
      if (typeof _price === "number" && Number.isFinite(_price) && _price > 0) {
        _rcPriceValue = _price;
      }
      if (typeof _currencyCode === "string" && _currencyCode.length === 3) {
        _rcPriceCurrency = _currencyCode.toUpperCase();
      }
    } catch { /* noop — completion event fires without value/currency */ }

    try {
      // Iter XX.229 — TEMPORARY diagnostic: package identity BEFORE
      // the native purchase call. Confirms the package we're about
      // to purchase is the SAME one whose price rendered in the UI.
      try {
        const _pkg_dbg = await getMonthlyPackage();
        // eslint-disable-next-line no-console
        console.log("[gully-purchase-diag] pre-purchase package", {
          package_identifier:    _pkg_dbg?.identifier ?? null,
          package_offering_id:   _pkg_dbg?.offeringIdentifier ?? null,
          product_identifier:    _pkg_dbg?.product?.identifier ?? null,
          product_price_string:  _pkg_dbg?.product?.priceString ?? null,
          product_price_num:     _pkg_dbg?.product?.price ?? null,
          product_currency_code: _pkg_dbg?.product?.currencyCode ?? null,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log("[gully-purchase-diag] pre-purchase package read failed",
          { error: String((e && e.message) || e) });
      }
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] calling purchasePlusMonthly()");
      // Iter XX.285f — canonical chain for native:
      // checkout_request_started (about to invoke the store purchase)
      // → checkout_opened (handed off to StoreKit / Play Billing —
      // the sheet is now in front of the user; the promise below
      // resolves only after the sheet dismisses, so this is the
      // closest observable to sheet presentation).
      const _nativeChainExtra = {
        entry:      entryPoint,
        channel:    "revenuecat",
        product_id: (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
        offer_id:   "plus_default_v1",
        source:     entryPoint,
        variant:    variant,
      };
      const _nativeReqKey = `native_iap_req:${Date.now() >> 12}`;
      _emitChainEvent("checkout_request_started", {
        milestoneKey: _nativeReqKey, extra: _nativeChainExtra,
      });
      const _purchasePromise = purchasePlusMonthly();
      _emitChainEvent("checkout_opened", {
        milestoneKey: _nativeReqKey,
        extra: { ..._nativeChainExtra, sheet: "storekit_play_billing" },
      });
      const res = await _purchasePromise;
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] purchasePlusMonthly returned", {
        ok:          !!res?.ok,
        plusActive:  !!res?.plusActive,
        reason:      res?.reason ?? null,
        error:       res?.error ?? null,
      });
      _inFlight = false;

      // Iter XX.241 — Canonical post-purchase reconciliation.
      // If StoreKit/Google Play returned a truthy customerInfo we
      // ALWAYS run `/api/entitlements/sync` before deciding what to
      // toast — even when `res.plusActive` is false. The store may
      // have completed the transaction while RC's own entitlement
      // cache lags; the backend's server-to-server RC REST fetch is
      // the authoritative resolver. We NEVER tell the user "purchase
      // failed" when the store already charged them.
      const _customerInfoReturned = !!res?.customerInfo;
      let _canonicalPlus = null;
      if (res?.ok && _customerInfoReturned) {
        try {
          const _rcId =
            res.customerInfo?.originalAppUserId ||
            res.customerInfo?.original_app_user_id ||
            null;
          const { data: canonical } = await api.post(
            "/entitlements/sync",
            { rc_app_user_id: _rcId || undefined },
          );
          _canonicalPlus = canonical?.gully_plus || null;
        } catch (_err) {
          // Backend sync errored — fall through to the RC-only
          // heuristic (`res.plusActive`). Never rethrow.
          // eslint-disable-next-line no-console
          console.log("[gully-purchase-diag] entitlements/sync failed", {
            error: String((_err && _err.message) || _err),
          });
        }
      }

      // Canonical PASS: backend reports gully_plus active.
      if (_canonicalPlus?.active) {
        _track("plus_purchased_native", { entry: entryPoint, source: "canonical" });
        // Iter XX.285f — canonical payment_completed (client-observed;
        // server confirms via purchase_completed on the RC webhook).
        _emitChainEvent("payment_completed", {
          milestoneKey: `native_iap_pay:${Date.now() >> 12}`,
          extra: {
            entry: entryPoint, channel: "revenuecat",
            product_id: (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
            offer_id: "plus_default_v1", source: entryPoint, variant,
            confirmation: "canonical_entitlements_sync",
          },
        });
        try {
          const info = res.customerInfo || {};
          const entitlements = info?.entitlements?.active || {};
          const active = Object.values(entitlements)[0] || {};
          logPurchaseCompleted({
            product_id: active.productIdentifier || "plus_monthly_ios",
            period: "monthly",
            transaction_id: active.originalPurchaseDate || active.latestPurchaseDate || undefined,
            value: _rcPriceValue,
            currency: _rcPriceCurrency,
          });
        } catch {}
        try {
          toast.success("Welcome to Gully Plus", {
            description: "Your subscription is active. Every coaching arc is unlocked.",
          });
        } catch { /* noop */ }
        return { ok: true, source: "native_iap" };
      }

      // Store confirmed the purchase (customerInfo present), backend
      // hasn't caught up yet. Show a "finalising" message — NEVER a
      // failure toast — and return { ok: true, syncing: true } so
      // callers can poll again if needed.
      if (res?.ok && _customerInfoReturned) {
        _track("plus_purchase_syncing_native", { entry: entryPoint });
        // Iter XX.285f — the store confirmed the transaction
        // (customerInfo present); backend sync just hasn't caught up.
        // Payment is complete from the user's perspective.
        _emitChainEvent("payment_completed", {
          milestoneKey: `native_iap_pay:${Date.now() >> 12}`,
          extra: {
            entry: entryPoint, channel: "revenuecat",
            product_id: (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
            offer_id: "plus_default_v1", source: entryPoint, variant,
            confirmation: "store_customer_info_pending_backend_sync",
          },
        });
        try {
          toast.message("Your purchase is finalising.", {
            description: "The App Store confirmed your subscription. Gully Plus will unlock in a moment — you don't need to buy it again.",
          });
        } catch { /* noop */ }
        // Best-effort second sync ~4s later; user may keep the page
        // open and see Plus flip active without any further action.
        setTimeout(() => {
          try { api.post("/entitlements/sync", {}).catch(() => {}); }
          catch { /* noop */ }
        }, 4000);
        return { ok: true, source: "native_iap_syncing", syncing: true };
      }

      if (res.ok && res.plusActive) {
        // Fallback branch — RC reported plusActive but canonical sync
        // wasn't called (customerInfo missing) OR backend response
        // shape was unexpected. Preserve legacy behaviour.
        _track("plus_purchased_native", { entry: entryPoint, source: "rc_only" });
        // Iter XX.285f — canonical payment_completed (RC-reported).
        _emitChainEvent("payment_completed", {
          milestoneKey: `native_iap_pay:${Date.now() >> 12}`,
          extra: {
            entry: entryPoint, channel: "revenuecat",
            product_id: (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
            offer_id: "plus_default_v1", source: entryPoint, variant,
            confirmation: "rc_plus_active_fallback",
          },
        });
        try {
          const info = res.customerInfo || {};
          const entitlements = info?.entitlements?.active || {};
          const active = Object.values(entitlements)[0] || {};
          logPurchaseCompleted({
            product_id: active.productIdentifier || "plus_monthly_ios",
            period: "monthly",
            transaction_id: active.originalPurchaseDate || active.latestPurchaseDate || undefined,
            value: _rcPriceValue,
            currency: _rcPriceCurrency,
          });
        } catch {}
        // Legacy refresh — kept for one iteration alongside the
        // canonical sync above. Removed in the XX.241 cleanup pass.
        try { await api.post("/billing/native/refresh", {}).catch(() => {}); }
        catch { /* noop */ }
        try {
          toast.success("Welcome to Gully Plus", {
            description: "Your subscription is active. Every coaching arc is unlocked.",
          });
        } catch { /* noop */ }
        return { ok: true, source: "native_iap" };
      }

      if (res.reason === "user_cancelled") {
        _track("plus_purchase_cancelled_native", { entry: entryPoint });
        try { logPurchaseCancelled({ product_id: "plus_monthly_ios", cancel_reason: "user_cancelled" }); } catch {}
        // Iter XX.285f — canonical checkout_cancelled (user dismissed
        // the store sheet). Distinct from checkout_failed: cancellation
        // is NOT a failure.
        _emitChainEvent("checkout_cancelled", {
          milestoneKey: `native_iap_cancel:${Date.now() >> 12}`,
          extra: {
            entry: entryPoint, channel: "revenuecat",
            product_id: (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
            offer_id: "plus_default_v1", source: entryPoint, variant,
            cancel_reason: "user_cancelled",
          },
        });
        return { ok: false, reason: "user_cancelled" };
      }
      if (res.reason === "pending") {
        _track("plus_purchase_pending_native", { entry: entryPoint });
        try {
          // Iter XX.214 — Platform-aware "pending" copy so iOS users
          // never see "Google Play" phrasing (Apple review + trust).
          toast.message("Payment is pending.", {
            description: isIOS()
              ? "The App Store will finish this purchase shortly. Your Plus will unlock automatically once it clears."
              : "Google Play will finish this purchase shortly. Your Plus will unlock automatically once it clears.",
          });
        } catch { /* noop */ }
        return { ok: false, reason: "pending" };
      }
      // Every other RC error path — treat as recoverable, don't
      // reveal internal reason codes to the user.
      // Iter XX.283 — Persist the normalized RevenueCat / Play / StoreKit
      // error_category (from `_normalizeRcErrorCategory`) on both:
      //   1) the internal `plus_purchase_failed_native` diagnostic, and
      //   2) the canonical `checkout_failed` funnel event.
      // Behavior-preserving — the same generic toast still renders.
      const _nativeErrCategory = res.error_category
        || (res.reason && res.reason !== "error" ? res.reason : "unknown");
      const _nativeErrDetails = res.error_details || {};
      // Iter XX.285e — Approved purchase-failure telemetry spec
      // (2026-09-26). Enrich plus_purchase_failed_native with platform,
      // app/build version, product, offering, error source, raw code,
      // sanitized message, and stage. All best-effort, never throws.
      const _telemetry = _gatherPurchaseTelemetry();
      _track("plus_purchase_failed_native", {
        entry:          entryPoint,
        reason:         res.reason || "unknown",
        error_category: _nativeErrCategory,
        // Include the raw signals so ops can still reach the
        // underlying RC code when investigating a specific actor.
        readable_error_code:  _nativeErrDetails.readable_error_code || null,
        error_code:           _nativeErrDetails.error_code ?? null,
        underlying_error_code: _nativeErrDetails.underlying_error_code || null,
        // --- Approved telemetry spec fields ---
        platform:       _telemetry.platform,
        app_version:    _telemetry.app_version,
        build_number:   _telemetry.build_number,
        product_id:     _telemetry.product_id,
        rc_offering_id: _nativeErrDetails.rc_offering_id || null,
        error_source:   _nativeErrDetails.error_source || "revenuecat",
        error_code_raw: _nativeErrDetails.error_code ?? null,
        error_message:  _sanitizeErrorMessage(_nativeErrDetails.error_message || res.message),
        stage:          _nativeErrDetails.stage || "pre_sheet",
      });
      // Canonical `checkout_failed` — surfaces the SAME normalized
      // category on the first-party funnel dataset Muse reads
      // (`acquisition_events`). Stripe web's checkout_failed emit
      // uses the same field name (`error_category`) so a unified
      // dashboard splits by category regardless of channel.
      try {
        trackMetaFunnelEvent("checkout_failed", {
          milestone_key: `native_iap_fail:${_nativeErrCategory}:${Date.now() >> 12}`,
          extra: {
            channel:        "revenuecat",
            product_id:     (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
            offer_id:       "plus_default_v1",
            source:         entryPoint,
            variant:        variant,
            error_category: _nativeErrCategory,
            // P0 Android pre-sheet investigation (2026-10-06): surface the
            // sanitized native error message on the canonical funnel event
            // so the next opaque failure is diagnosable via the Muse
            // purchase-chain read path. Diagnostic-only; the
            // plus_purchase_failed_native event carries the same signal
            // but is orphaned from the actor on native (session-only
            // identity). Reuses the approved XX.285e sanitizer.
            error_message:  _sanitizeErrorMessage(
              _nativeErrDetails.message || _nativeErrDetails.error_message
              || res.error || res.message),
          },
        });
      } catch { /* fire-and-forget */ }
      try {
        toast.error("Couldn't complete that purchase.", {
          description: "No charge was made. Please try again in a moment.",
        });
      } catch { /* noop */ }
      return { ok: false, reason: res.reason || "native_purchase_error" };
    } catch (e) {
      _inFlight = false;
      // Iter XX.229 — TEMPORARY diagnostic: any exception escaping
      // purchasePlusMonthly is a JS/bridge-level failure before RC
      // ever surfaced a structured `{ok, reason}` result.
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] purchasePlusMonthly threw", {
        message:    String((e && e.message) || e),
        name:       (e && e.name) || null,
        stack_head: String(((e && e.stack) || "")).split("\n").slice(0, 3).join(" | "),
      });
      // Iter XX.283 — normalise even the bridge-level exception so we
      // never lose the shape distinction ("configuration" vs "unknown").
      // Iter XX.285e — include approved telemetry spec fields.
      const _excTelemetry = _gatherPurchaseTelemetry();
      _track("plus_purchase_failed_native", {
        entry:          entryPoint,
        reason:         "exception",
        error_category: "unknown",
        platform:       _excTelemetry.platform,
        app_version:    _excTelemetry.app_version,
        build_number:   _excTelemetry.build_number,
        product_id:     _excTelemetry.product_id,
        rc_offering_id: null,
        error_source:   "client",
        error_code_raw: null,
        error_message:  _sanitizeErrorMessage(e && e.message),
        stage:          "pre_sheet",
      });
      try {
        trackMetaFunnelEvent("checkout_failed", {
          milestone_key: `native_iap_exception:${Date.now() >> 12}`,
          extra: {
            channel:        "revenuecat",
            product_id:     (() => { try { return isIOS() ? "plus_monthly_ios" : "plus_monthly_android"; } catch { return "plus_monthly_native"; } })(),
            offer_id:       "plus_default_v1",
            source:         entryPoint,
            variant:        variant,
            error_category: "unknown",
            // P0 Android pre-sheet investigation (2026-10-06): same
            // diagnostic rationale as the native_iap_fail emission above —
            // a bridge-level exception escaping purchasePlusMonthly must
            // also leave its sanitized message on the canonical event.
            error_message:  _sanitizeErrorMessage(e && e.message),
          },
        });
      } catch { /* fire-and-forget */ }
      return { ok: false, reason: "native_purchase_error" };
    }
  }

  const authed = _hasToken();
  _track("plus_cta_clicked", { entry: entryPoint, authed });

  // --- Anonymous branch --------------------------------------------
  if (!authed) {
    _track("plus_identity_required", { entry: entryPoint });
    try { localStorage.setItem("gully_last_identity_trigger", "paywall"); }
    catch { /* noop */ }
    try {
      localStorage.setItem(
        "gully_plus_purchase_entry",
        JSON.stringify({ entryPoint, at: _now() }),
      );
    } catch { /* noop */ }
    // The router: Chat.jsx picks up ?upgrade=1 and opens IdentitySheet
    // in paywall-continuation mode. On successful link, Chat.jsx
    // redirects to /upgrade?from=paywall&autostart=1 which fires
    // startCheckout again — this time as an authed user.
    window.location.href = "/chat?upgrade=1";
    // Leave _inFlight true — the page is unloading. If for some
    // reason the redirect fails, the beforeunload will clear the
    // guard anyway when the JS context resets.
    return { ok: true, branch: "identity_first" };
  }

  // --- Authed branch -----------------------------------------------
  if (!priceLoaded) {
    _inFlight = false;
    toast.error("Pricing isn't loaded yet — give it a moment.");
    return { ok: false, reason: "price_not_loaded" };
  }

  _track("checkout_session_requested", { entry: entryPoint });
  // Iter XX.198 — canonical purchase CTA tap (Stripe web path).
  // Iter XX.282 — enriched with product_id / offer_id / source /
  // variant so Muse / XX.277 can split the funnel by paywall variant.
  // Iter XX.285f — TERMINOLOGY FIX: this event means "CTA tapped",
  // NOT "checkout started". Dual-emits deprecated `purchase_started`
  // alias until 2026-10-09.
  const _webCtaExtra = {
    entry:      entryPoint,
    channel:    "stripe",
    product_id: "plus_monthly_stripe",
    offer_id:   "plus_default_v1",
    source:     entryPoint,
    variant:    variant,
  };
  _emitCtaTapped({ milestoneKey: "stripe_checkout", extra: _webCtaExtra });
  // Iter XX.285f — checkout_request_started: the checkout ATTEMPT
  // metric. Fires immediately before POST /billing/checkout. This —
  // not the CTA tap — is what funnel reporting counts as a checkout
  // attempt.
  _emitChainEvent("checkout_request_started", {
    milestoneKey: `stripe_req:${Date.now() >> 12}`,
    extra: _webCtaExtra,
  });
  try {
    const { data } = await api.post("/billing/checkout", {
      origin_url: window.location.origin,
      source: entryPoint,
      country: country || undefined,
      variant: variant || undefined,
    });
    if (data?.url) {
      _track("checkout_session_created", {
        entry: entryPoint,
        session_id: data.session_id || null,
        reused: Boolean(data.reused),
      });
      _track("checkout_redirect_started", { entry: entryPoint });
      try { markCheckoutOpened({ source: entryPoint }); } catch { /* noop */ }
      window.location.href = data.url;
      return { ok: true, branch: "stripe_redirect" };
    }
    _inFlight = false;
    _track("checkout_creation_failed", { entry: entryPoint, reason: "no_url" });
    // Iter XX.285f — canonical checkout_failed on the no-URL path with
    // a normalized error_category.
    _emitChainEvent("checkout_failed", {
      milestoneKey: `stripe_fail:${Date.now() >> 12}`,
      extra: { ..._webCtaExtra, error_category: "no_checkout_url", reason: "no_url" },
    });
    toast.error("Couldn't start checkout. Please try again.");
    return { ok: false, reason: "no_url" };
  } catch (e) {
    _inFlight = false;
    const detail = e?.response?.data?.detail || "";
    // Iter XX.72.5 preserved — never surface a raw auth error.
    if (detail === "not_authenticated" || e?.response?.status === 401) {
      _track("plus_identity_required", { entry: "checkout_401_fallback" });
      try { await clearToken(); } catch { /* noop */ }
      window.location.href = "/chat?upgrade=1";
      return { ok: true, branch: "identity_first_401_fallback" };
    }
    _track("checkout_creation_failed", {
      entry: entryPoint,
      reason: detail || String(e?.message || "unknown"),
    });
    // Iter XX.285f — canonical checkout_failed on the web exception
    // path. 401s are excluded above (identity-first redirect, not a
    // checkout failure).
    _emitChainEvent("checkout_failed", {
      milestoneKey: `stripe_fail:${Date.now() >> 12}`,
      extra: {
        ..._webCtaExtra,
        error_category: e?.response?.status ? `http_${e.response.status}` : "request_error",
        error_message: _sanitizeErrorMessage(detail || (e && e.message)),
      },
    });
    toast.error(
      detail && detail !== "not_authenticated"
        ? detail
        : "Something went wrong starting your checkout. Please try again.",
    );
    return { ok: false, reason: detail || "unknown" };
  }
}

/**
 * Consume the entry-point stashed by an anonymous purchase before it
 * left the page. Called by Upgrade.jsx's autostart useEffect on
 * successful post-identity return so we can attribute the completed
 * funnel to the ORIGINAL entry point (not "plus_page").
 */
export function consumePlusPurchaseEntry() {
  try {
    const raw = localStorage.getItem("gully_plus_purchase_entry");
    if (!raw) return null;
    localStorage.removeItem("gully_plus_purchase_entry");
    const parsed = JSON.parse(raw);
    // Only trust a stash from the last 15 minutes.
    if (!parsed?.at || (_now() - parsed.at) > 15 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Manually clear the in-flight guard. Used when the identity sheet is
 * cancelled without leaving the SPA, and by the successful autostart
 * effect once its round-trip completes.
 */
export function resetPurchaseInFlight() { _inFlight = false; }
